import {
  Component,
  OnInit,
} from "@angular/core";
import {
  IQuestionBankQuestion,
  IQuestionBank,
  IQuestionEssaySubQuestion,
  IQuestionOptions,
} from "src/app/interface/dto";
import { QuestionBankService } from "../question-bank.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SnackbarService } from "src/app/services/snackbar.service";
import { Location } from '@angular/common';
import { ExamService } from "src/app/services/exam-service.service";

@Component({
  selector: "app-create-update-question",
  templateUrl: "./create-update-question.component.html",
  styleUrls: ["./create-update-question.component.scss"],
})
export class CreateUpdateQuestionComponent implements OnInit {
  textDisplay = "";
  modelError = "";
  categoryList: string[] = [];

  question: IQuestionBankQuestion = {
    id: 0,
    questionBankQuestionID: 0,
    questionType: "",
    language: "",
    languageEnum: "",
    questionHtml: "",
    category: "",
    marks: 0,
    maxWordCount: 0,
    penaltyMarks: 0,
    disabled: false,
    options: [],
    saqPossibleAnswers: "",
    essaySubQuestions: [],
    questionBankID: 0,
  };

  isSave = true;
  questionBank?: IQuestionBank;

  constructor(
    private questionBankService: QuestionBankService,
    private snackBar: SnackbarService,
    private actRoute: ActivatedRoute,
    private examService: ExamService,
    private _location: Location
  ) {}

  languages: any = [
    { lang: "English", code: "en" },
    { lang: "Français", code: "fr" },
    { lang: "Not Set", code: "NotSet" },
  ];

  questionTypes: any = [
    { label: "Multiple-Choice", value: "OptionsOnly" },
    { label: "Short Answer Question", value: "SAQ" },
    { label: "Essay", value: "Essay" },
  ];

  async ngOnInit() {
    await this.loadCategories();
    
    this.actRoute.paramMap.subscribe(async (params) => {
      this.question.questionBankID = Number(params.get("questionBankID"));
      this.question.questionBankQuestionID =
        (JSON.parse(params.get("questionid") || "0") as number) ?? 0;
      await this.loadQuestionBankAndPrefill();
      if (this.question.questionBankQuestionID !== 0 && this.question.questionBankQuestionID !== null) {
        this.textDisplay = `${this.questionBank?.name} - Update Question`;
        this.isSave = false;
        await this.getQuestionDetails();
      } else {
        this.textDisplay = `${this.questionBank?.name} - Add New Question`;
        this.pushOption();
      }
    });
  }

   async loadCategories() {
    try {
      await this.examService.getQuestionCategories().then((c: any) => {
        this.categoryList = c;
      });
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }

  resetForm() {
    this.question = {
      id: 0,
      questionBankQuestionID: 0,
      questionType: this.question.questionType,
      language: "",
      languageEnum: "",
      questionHtml: "",
      category: "",
      marks: 0,
      maxWordCount: 0,
      penaltyMarks: 0,
      disabled: false,
      options: [],
      saqPossibleAnswers: "",
      saqAnswersList: [],
      essaySubQuestions: [],
      questionBankID: this.question.questionBankID,
    };
  }

  addOption() {
    const newOption: IQuestionOptions = {
      questionBankQuestionID: this.question.questionBankQuestionID,
      ID: Date.now(),
      optionHtml: "",
      answer: false,
    };
    this.question.options.push(newOption);
  }

  addEssaySubQuestion() {
    const newEssay: IQuestionEssaySubQuestion = {
      questionBankQuestionID: this.question.questionBankQuestionID,
      questionEssaySubQuestionID: Date.now(),
      questionHtml: "",
      questionNoLabel: "",
      marks: 0,
    };
    this.question.essaySubQuestions.push(newEssay);
  }

  onQuestionTypeChange() {
    // Clear options when switching question types

    this.question.questionType =
      this.question.questionType === "Multiple-Choice"
        ? "OptionsOnly"
        : this.question.questionType === "Short Answer Question"
        ? "SAQ"
        : this.question.questionType;
    if (
      this.question.questionType === "OptionsOnly" &&
      this.question.options.length === 0
    ) {
      this.pushOption();
    }
  }

  pushOption() {
    if (!this.question.options) {
      this.question.options = [];
    }

    this.question.options.push({
      questionBankQuestionID: 0,
      ID: 0,
      optionHtml: "",
      answer: false,
    });
  }

  removeOption(index: number) {
    const halfBeforeTheUnwantedElement = this.question.options.slice(0, index);
    index += 1;
    const halfAfterTheUnwantedElement = this.question.options.slice(index);
    this.question.options = halfBeforeTheUnwantedElement.concat(
      halfAfterTheUnwantedElement
    );
  }

  pushEssaySubQuestion() {
    if (!this.question.essaySubQuestions) {
      this.question.essaySubQuestions = [];
    }

    const index = this.question.essaySubQuestions.length;

    const newEssaySubQuestion: IQuestionEssaySubQuestion = {
      questionBankQuestionID: this.question.questionBankQuestionID,
      questionEssaySubQuestionID: 0,
      questionHtml: "",
      questionNoLabel: this.getLetter(index),
      marks: 0,
    };

    this.question.essaySubQuestions.push(newEssaySubQuestion);
  }

  getLetter(index: number): string {
    return String.fromCharCode(97 + index); // 97 = 'a'
  }

  removeEssaySubQuestion() {
    if (
      !this.question.essaySubQuestions ||
      this.question.essaySubQuestions.length === 0
    ) {
      return;
    }

    const essaySubQuestion = this.question.essaySubQuestions;
    this.question.essaySubQuestions = essaySubQuestion.slice(
      0,
      essaySubQuestion.length - 1
    );
  }

  async saveQuestion(update: boolean = false) {
    this.validateModel();
    if (this.modelError !== "") {
      window.document.getElementById("modelError")?.focus();
      window.document.getElementById("modelError")?.blur();
      return;
    }

    if (update) {
      this.onQuestionTypeChange()
      await this.questionBankService.updateQuestion(this.question);
      this.snackBar.success("Question Updated Successfully!");
    } else {
      await this.questionBankService.createQuestion(this.question);
      this.snackBar.success("Question Saved Successfully!");
      this.resetForm();
    }
  }

  validateModel() {
    this.question.options = this.question.options?.filter(
      (q) => q.optionHtml !== ""
    );

    this.modelError = "";

    if (this.question.marks === 0) {
      this.modelError += "\n*Marks required \n";
    }

    if (this.question.questionHtml === "") {
      this.modelError += "\n*Question required \n";
    }

    // Validation for multiple choice questions
    if (this.question.questionType === 'OptionsOnly' || this.question.questionType === 'Multiple-Choice') {
      if (!this.question.options?.find((q) => q.answer === true)) {
        this.modelError += "\n*No answer Selected \n";
      }
      if (
        this.question.options?.find(
          (q) => q.answer === true && q.optionHtml === ""
        )
      ) {
        this.modelError += "\n*Answer has no value \n";
      }
      if (this.question.options?.length < 2) {
        this.modelError += "\n*Must have at least 2 options \n";
      }
    }

    // Validation for essay sub-questions
    if (this.question.questionType === "Essay") {
      let totalEssaySubQuestionMarks = 0;

      this.question.essaySubQuestions?.forEach((subQuestion, index) => {
        const label = subQuestion.questionNoLabel || this.getLetter(index);

        if (this.essayAnswerIsNotBlank(subQuestion.questionHtml) === "") {
          this.modelError += `\n* Essay Sub-Question ${label}. content is required\n`;
        }

        if (subQuestion.marks === null || subQuestion.marks === 0) {
          this.modelError += `\n* Essay Sub-Question ${label}. marks is required\n`;
        } else {
          totalEssaySubQuestionMarks += subQuestion.marks;
        }
      });

      if (totalEssaySubQuestionMarks > this.question.marks) {
        this.modelError += `\n* Total marks of Essay Sub-Questions (${totalEssaySubQuestionMarks}) exceed main question marks (${this.question.marks})\n`;
      }
    }
  }

  async getQuestionDetails() {
    if (!this.isSave && this.question.questionBankQuestionID) {
      try {
        this.question = await this.questionBankService.getQuestion(
          this.question.questionBankQuestionID
        );

        this.question.language = this.question.languageEnum;
      } catch (error) {
        console.error("Error loading question details:", error);
        this.snackBar.error("Failed to load question details");
      }
    }
  }

  essayAnswerIsNotBlank(htmlString: string): string {
    if (!htmlString || !htmlString.trim()) {
      return "";
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    tempDiv
      .querySelectorAll("script, style")
      .forEach((element) => element.remove());

    return tempDiv.textContent?.trim() || "";
  }

  async loadQuestionBankAndPrefill() {
    try {
      this.questionBank = await this.questionBankService.getQuestionBank(
        this.question.questionBankID
      );

      this.question.questionType = this.questionBank.questionType;
    } catch (error) {
      console.error("Error loading question bank:", error);
    }
  }

   goBack() {
    this._location.back();
  }
}
